import { useCallback, useEffect, useState } from "react";
import { listOpsDeals } from "@/lib/opsPaperworkClient";
import {
  DEFAULT_DEALS_LIST_STATUS,
  DEFAULT_DEALS_PAGE_SIZE,
  DEALS_PAGE_SIZES,
  type DealsListFacets,
  type DealsListStatus,
  type DealsPageSize,
  type DealsTypeMatchFilter,
} from "@/lib/ops/opsDealsListQuery";
import type { DealRow } from "./opsPaperworkTypes";

const EMPTY_FACETS: DealsListFacets = { salespeople: [], offices: [] };

export function useOpsDealsList(opts: { active: boolean }) {
  const [status, setStatusState] = useState<DealsListStatus>(DEFAULT_DEALS_LIST_STATUS);
  const [salesperson, setSalespersonState] = useState("");
  const [office, setOfficeState] = useState("");
  const [typeMatch, setTypeMatchState] = useState<DealsTypeMatchFilter>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState<DealsPageSize>(DEFAULT_DEALS_PAGE_SIZE);
  const [deals, setDeals] = useState<DealRow[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<DealsListFacets>(EMPTY_FACETS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const r = await listOpsDeals({
      status,
      salesperson: salesperson || undefined,
      office: office || undefined,
      type_match: typeMatch || undefined,
      page,
      page_size: pageSize,
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setDeals([]);
      setTotal(0);
      return;
    }
    setError("");
    setDeals((r.data.deals ?? []) as DealRow[]);
    setTotal(typeof r.data.total === "number" ? r.data.total : (r.data.deals ?? []).length);
    if (typeof r.data.page === "number" && r.data.page !== page) setPage(r.data.page);
    setFacets(r.data.facets ?? EMPTY_FACETS);
  }, [status, salesperson, office, typeMatch, page, pageSize]);

  useEffect(() => {
    if (!opts.active) return;
    void load();
  }, [opts.active, load]);

  const setStatus = useCallback((next: DealsListStatus) => {
    setStatusState(next);
    setPage(1);
  }, []);
  const setSalesperson = useCallback((next: string) => {
    setSalespersonState(next);
    setPage(1);
  }, []);
  const setOffice = useCallback((next: string) => {
    setOfficeState(next);
    setPage(1);
  }, []);
  const setTypeMatch = useCallback((next: DealsTypeMatchFilter) => {
    setTypeMatchState(next);
    setPage(1);
  }, []);
  const setPageSize = useCallback((next: DealsPageSize) => {
    setPageSizeState(next);
    setPage(1);
  }, []);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstShown = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastShown = Math.min(total, page * pageSize);

  return {
    status,
    setStatus,
    salesperson,
    setSalesperson,
    office,
    setOffice,
    typeMatch,
    setTypeMatch,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
    firstShown,
    lastShown,
    deals,
    total,
    facets,
    loading,
    error,
    reload: load,
    pageSizeOptions: DEALS_PAGE_SIZES,
  };
}

export type OpsDealsListModel = ReturnType<typeof useOpsDealsList>;
